/**
 * Carrier identifier parsing for the lookup edge functions.
 *
 * The carrier-lookup (US) and carrier-lookup-ca edge functions expect
 * structured bodies with explicit field names · never a generic
 * `identifier`. This helper takes a free-text identifier from the user
 * and produces the body shape each function requires, plus a
 * human-readable label of what was detected so the UI can confirm.
 *
 * If a prefix is present in the input, it always wins. If the input is
 * bare digits, the caller can pass `assumeKind` (driven by a UI toggle)
 * to tell the parser what to assume. With no toggle and no prefix, the
 * parser returns an error asking for one.
 *
 * Supported formats:
 *   US: MC-123456, MX-123456, FF-123456, DOT-1234567 (or USDOT/US-DOT)
 *   CA: CVOR-123-456-789, CTQ-12345, NEQ-12345, NSC-12345,
 *       DOT-1234567 (cross-border)
 */

const PREFIX_HINT =
  "Pick a type above, or type a prefix: MC-123456, DOT-1234567, CVOR-123-456-789 (ON), CTQ-12345 (QC), NSC-12345.";

export type Jurisdiction = "US" | "CA";
export type IdentifierKind = "MC" | "DOT" | "CVOR" | "CTQ" | "NSC";

export interface UsLookupBody {
  mc_number?: string;
  dot_number?: string;
}

export interface CaLookupBody {
  cvor_number?: string;
  ctq_number?: string;
  nsc_number?: string;
  dot_number?: string;
  province: string;
}

export interface ParsedIdentifier {
  body: UsLookupBody | CaLookupBody;
  detected: string;
  jurisdiction: Jurisdiction;
}

export interface ParseError {
  error: string;
  hint: string;
}

const VALID_CA_PROVINCES = new Set([
  "AB",
  "BC",
  "MB",
  "NB",
  "NL",
  "NS",
  "NT",
  "NU",
  "ON",
  "PE",
  "QC",
  "SK",
  "YT",
]);

const PROVINCE_NAMES: Record<string, string> = {
  AB: "Alberta",
  BC: "British Columbia",
  MB: "Manitoba",
  NB: "New Brunswick",
  NL: "Newfoundland and Labrador",
  NS: "Nova Scotia",
  NT: "Northwest Territories",
  NU: "Nunavut",
  ON: "Ontario",
  PE: "Prince Edward Island",
  QC: "Quebec",
  SK: "Saskatchewan",
  YT: "Yukon",
};

function digitsOnly(s: string): string {
  return s.replace(/\D/g, "");
}

function normalize(raw: string): string {
  return raw.toUpperCase().replace(/[\s_]/g, "");
}

/**
 * Lightweight prefix-only check for Canadian identifiers. Used by surfaces
 * (hero quick-verify, trust-link dialog) that only need to branch the UI on
 * "is this CA?" without running the full parser.
 *
 * Returns the kind so the caller can render a kind-specific prompt
 * (CVOR vs CTQ vs NSC). Province-prefixed NSC (e.g. AB12345) also
 * counts as Canadian.
 */
export function detectCanadianIdentifier(raw: string): {
  isCanadian: boolean;
  kind?: "CVOR" | "CTQ" | "NSC";
} {
  const cleaned = normalize(raw.trim());
  if (!cleaned) return { isCanadian: false };
  if (cleaned.startsWith("CVOR")) return { isCanadian: true, kind: "CVOR" };
  if (cleaned.startsWith("CTQ") || cleaned.startsWith("NEQ"))
    return { isCanadian: true, kind: "CTQ" };
  if (cleaned.startsWith("NSC")) return { isCanadian: true, kind: "NSC" };
  const provinceMatch = cleaned.match(/^([A-Z]{2})\d+$/);
  if (
    provinceMatch &&
    VALID_CA_PROVINCES.has(provinceMatch[1]) &&
    provinceMatch[1] !== "ON" &&
    provinceMatch[1] !== "QC"
  ) {
    return { isCanadian: true, kind: "NSC" };
  }
  return { isCanadian: false };
}

/**
 * Parse with explicit jurisdiction (legacy, still used by vernot pages).
 */
export function parseCarrierIdentifier(
  raw: string,
  jurisdiction: Jurisdiction,
): ParsedIdentifier | ParseError {
  const trimmed = raw.trim();
  if (!trimmed) {
    return {
      error: "Enter a carrier identifier to verify.",
      hint:
        jurisdiction === "CA"
          ? "Prefix is required. Try CVOR-123-456-789 (Ontario), CTQ-12345 (Quebec), or DOT-1234567 for cross-border."
          : "Prefix is required. Try MC-123456 or DOT-1234567.",
    };
  }

  const cleaned = normalize(trimmed);

  if (jurisdiction === "US") {
    return parseUs(cleaned);
  }
  return parseCa(cleaned);
}

/**
 * Auto-detect jurisdiction from the identifier format.
 *
 * If a prefix is present, it wins. Otherwise the optional `assumeKind`
 * (driven by a UI toggle) is used to interpret bare digits. With no
 * prefix and no toggle, the parser returns an error.
 *
 * Detection rules:
 *   MC / MX / FF prefix       → US (FMCSA docket number)
 *   DOT / USDOT / US-DOT      → US (FMCSA DOT number)
 *   CVOR prefix                → CA Ontario
 *   CTQ / NEQ prefix           → CA Quebec
 *   NSC prefix                 → CA (federal / other provinces)
 *   Bare digits + assumeKind  → that kind
 *   Bare digits, no toggle    → error with helpful hint
 *
 * `provinceHint` is consulted only when the kind is NSC and the input
 * doesn't carry its own province (i.e. bare digits). It lets the UI
 * resolve "we know it's NSC but the user pasted bare digits" without
 * forcing them to retype with a province prefix.
 */
export function autoParseCarrierIdentifier(
  raw: string,
  assumeKind?: IdentifierKind,
  provinceHint?: string,
): ParsedIdentifier | ParseError {
  const trimmed = raw.trim();
  if (!trimmed) {
    return {
      error: "Enter a carrier number to verify.",
      hint: PREFIX_HINT,
    };
  }

  const cleaned = normalize(trimmed);

  // Canadian prefixes first (more specific)
  if (cleaned.startsWith("CVOR")) return parseCa(cleaned);
  if (cleaned.startsWith("CTQ") || cleaned.startsWith("NEQ")) return parseCa(cleaned);
  if (cleaned.startsWith("NSC")) return parseCa(cleaned);

  // US prefixes
  if (cleaned.startsWith("MC") || cleaned.startsWith("MX") || cleaned.startsWith("FF")) {
    return parseUs(cleaned);
  }
  if (
    cleaned.startsWith("DOT") ||
    cleaned.startsWith("USDOT") ||
    cleaned.startsWith("US-DOT")
  ) {
    return parseUs(cleaned);
  }

  // Quebec RIN (registry identifier · R + 7-8 digits). RIN is the registry
  // ID, not a permit number · the lookup function expects CTQ permit / NEQ.
  // Surface a clear hint instead of silently routing to CTQ.
  const rinMatch = cleaned.match(/^R(\d{7,8})$/);
  if (rinMatch) {
    return {
      error: "RIN numbers can't be looked up directly.",
      hint: "RIN is the QC registry ID. Use the carrier's CTQ permit (CTQ-12345) or NEQ (NEQ-1234567890) instead.",
    };
  }

  // Province-prefixed NSC · e.g. MB0185025, AB123456, BC987654, MB-0185025.
  // Two-letter province code + digits maps to NSC for that province.
  // ON / QC are excluded · those provinces use CVOR / CTQ, not NSC.
  // Strip dashes here so MB-0185025 matches the same regex as MB0185025;
  // dispatchers paste either form.
  const provincePrefixMatch = cleaned.replace(/-/g, "").match(/^([A-Z]{2})(\d+)$/);
  if (provincePrefixMatch && VALID_CA_PROVINCES.has(provincePrefixMatch[1])) {
    const province = provincePrefixMatch[1];
    const digits = provincePrefixMatch[2];
    if (province === "ON") {
      return {
        error: "Ontario carriers use CVOR, not NSC.",
        hint: "Try CVOR-123-456-789 (9 digits).",
      };
    }
    if (province === "QC") {
      return {
        error: "Quebec carriers use CTQ permits, not NSC.",
        hint: "Try CTQ-12345 or NEQ-1234567890.",
      };
    }
    return {
      body: { nsc_number: digits, province },
      detected: `NSC ${province}-${digits} · ${PROVINCE_NAMES[province]}`,
      jurisdiction: "CA",
    };
  }

  // No recognized prefix. If the toggle gave us a kind, use it on bare digits.
  const digits = digitsOnly(cleaned);
  const isBareDigits = digits.length > 0 && digits === cleaned.replace(/-/g, "");
  if (isBareDigits && assumeKind) {
    // NSC + bare digits + provinceHint: skip the synthesized "NSC<digits>"
    // path that would re-fail the province-needed check, and emit the parsed
    // body directly. ON/QC use CVOR/CTQ instead so block them here.
    if (assumeKind === "NSC" && provinceHint) {
      const province = provinceHint.toUpperCase();
      if (!VALID_CA_PROVINCES.has(province)) {
        return {
          error: `Unknown province "${provinceHint}".`,
          hint: "Use a two-letter Canadian province code (e.g. MB, AB, BC).",
        };
      }
      if (province === "ON" || province === "QC") {
        return {
          error: province === "ON"
            ? "Ontario carriers use CVOR, not NSC."
            : "Quebec carriers use CTQ permits, not NSC.",
          hint: province === "ON"
            ? "Switch to the CVOR field (9 digits)."
            : "Switch to the CTQ field (4-8 digits).",
        };
      }
      return {
        body: { nsc_number: digits, province },
        detected: `NSC ${province}-${digits} · ${PROVINCE_NAMES[province]}`,
        jurisdiction: "CA",
      };
    }
    const synthesized = `${assumeKind}${digits}`;
    if (assumeKind === "MC" || assumeKind === "DOT") return parseUs(synthesized);
    return parseCa(synthesized);
  }
  if (isBareDigits) {
    return {
      error: "Pick a type above or add a prefix.",
      hint: PREFIX_HINT,
    };
  }

  return {
    error: "Unrecognized carrier number format.",
    hint: PREFIX_HINT,
  };
}

function parseUs(cleaned: string): ParsedIdentifier | ParseError {
  // DOT-prefix
  if (
    cleaned.startsWith("DOT") ||
    cleaned.startsWith("USDOT") ||
    cleaned.startsWith("US-DOT")
  ) {
    const digits = digitsOnly(cleaned);
    if (!digits) {
      return {
        error: "DOT number is missing digits.",
        hint: "Format: DOT-1234567",
      };
    }
    if (digits.length > 8) {
      return {
        error: `DOT number too long (${digits.length} digits).`,
        hint: "DOT numbers are 1-8 digits.",
      };
    }
    return {
      body: { dot_number: digits },
      detected: `US DOT-${digits}`,
      jurisdiction: "US",
    };
  }

  // MC, MX, FF prefix → docket number
  if (
    cleaned.startsWith("MC") ||
    cleaned.startsWith("MX") ||
    cleaned.startsWith("FF")
  ) {
    const digits = digitsOnly(cleaned);
    if (!digits) {
      return {
        error: "MC number is missing digits.",
        hint: "Format: MC-123456",
      };
    }
    if (digits.length > 8) {
      return {
        error: `MC number too long (${digits.length} digits).`,
        hint: "MC numbers are 1-8 digits.",
      };
    }
    return {
      body: { mc_number: digits },
      detected: `US MC-${digits}`,
      jurisdiction: "US",
    };
  }

  // No recognized prefix · bare digits are ambiguous (MC vs DOT).
  return {
    error: "Add a prefix so we know whether this is an MC or DOT number.",
    hint: "Use MC-123456 or DOT-1234567.",
  };
}

function parseCa(cleaned: string): ParsedIdentifier | ParseError {
  // Cross-border DOT
  if (
    cleaned.startsWith("DOT") ||
    cleaned.startsWith("USDOT") ||
    cleaned.startsWith("US-DOT")
  ) {
    const digits = digitsOnly(cleaned);
    if (!digits) {
      return {
        error: "DOT number is missing digits.",
        hint: "Format: DOT-1234567",
      };
    }
    if (digits.length > 8) {
      return {
        error: `DOT number too long (${digits.length} digits).`,
        hint: "DOT numbers are 1-8 digits.",
      };
    }
    // Cross-border: pick Ontario as the default province since Bruno is Ontario-based.
    // Province dropdown is a Phase 1.5 enhancement.
    return {
      body: { dot_number: digits, province: "ON" },
      detected: `Cross-border DOT-${digits} (province defaulted to Ontario)`,
      jurisdiction: "CA",
    };
  }

  // Ontario CVOR
  if (cleaned.startsWith("CVOR")) {
    const digits = digitsOnly(cleaned);
    if (digits.length !== 9) {
      return {
        error: `CVOR numbers are 9 digits (got ${digits.length}).`,
        hint: "Format: CVOR-123-456-789",
      };
    }
    return {
      body: { cvor_number: digits, province: "ON" },
      detected: `Ontario CVOR-${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)}`,
      jurisdiction: "CA",
    };
  }

  // Quebec CTQ
  if (cleaned.startsWith("CTQ") || cleaned.startsWith("NEQ")) {
    const digits = digitsOnly(cleaned);
    if (digits.length < 4 || digits.length > 8) {
      return {
        error: `CTQ permit numbers are 4-8 digits (got ${digits.length}).`,
        hint: "Format: CTQ-12345",
      };
    }
    return {
      body: { ctq_number: digits, province: "QC" },
      detected: `Quebec CTQ-${digits}`,
      jurisdiction: "CA",
    };
  }

  // NSC (National Safety Code · issued per-province across Canada).
  // Try to extract the province from the value · NSC-MB-12345 or NSCMB12345.
  // Without a province context the lookup can't reach the right registry,
  // so we emit a helpful error instead of silently defaulting to NS.
  if (cleaned.startsWith("NSC")) {
    const rest = cleaned.slice(3).replace(/-/g, "");
    if (!rest) {
      return {
        error: "NSC number is missing.",
        hint: "Format: NSC-MB-12345 (province in the number).",
      };
    }
    const provinceMatch = rest.match(/^([A-Z]{2})(\d+)$/);
    if (provinceMatch && VALID_CA_PROVINCES.has(provinceMatch[1])) {
      const province = provinceMatch[1];
      const digits = provinceMatch[2];
      return {
        body: { nsc_number: digits, province },
        detected: `NSC ${province}-${digits} · ${PROVINCE_NAMES[province]}`,
        jurisdiction: "CA",
      };
    }
    const digits = digitsOnly(rest);
    if (!digits) {
      return {
        error: "NSC number is missing digits.",
        hint: "Format: NSC-MB-12345",
      };
    }
    return {
      error: "NSC numbers need a province · we don't know which registry to query.",
      hint: `Add the province code · e.g. NSC-MB-${digits} (Manitoba), NSC-AB-${digits} (Alberta), NSC-BC-${digits} (British Columbia).`,
    };
  }

  // No recognized prefix · bare digits are ambiguous (CVOR vs CTQ vs NSC vs DOT).
  return {
    error: "Add a prefix so we know what kind of number this is.",
    hint: "Use CVOR-123-456-789 (Ontario), CTQ-12345 (Quebec), NSC-12345, or DOT-1234567 for cross-border.",
  };
}

export function isParseError(
  result: ParsedIdentifier | ParseError,
): result is ParseError {
  return "error" in result;
}

export { VALID_CA_PROVINCES };
